import { HELP_CENTER_URL, isDefined } from '@aloft/toolkit-shared';
import { BorderStyleProperty, Button, ButtonProps, Flex, Typography } from '@aloft/toolkit-sui';

import { useLocation, useParams } from 'react-router';

import { shouldShowHeaderNavigationForV2 } from '../../layout/shouldShowHeaderNavigationForV2.tsx';
import { selectAccount } from '../../redux/features/selectors/accountSelectors.ts';
import { selectDeviceWidth } from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { borderColor, borderStyle, borderWidth } from '../../styles/border.ts';
import { EnvironmentFilter } from '../EnvironmentFilter/EnvironmentFilter.tsx';
import { Tkv2ReferenceBarMobileToggle } from '../ReferenceBar/Tkv2RefBarMobileToggle.tsx';
import { useIncludeHeaderBottomPadding } from './HeaderNav/useIncludeHeaderBottomPadding.ts';
import { MainHeaderOrderNavigation } from './MainHeaderOrderNavigation.tsx';
import { MainHeaderProfileMenu } from './MainHeaderProfileMenu.tsx';
import { MainHeaderTitle } from './MainHeaderTitle.tsx';
import { SUBNAV_HEIGHT } from './consts.ts';

import './MainHeader.scss';

type MainHeaderProps = {
  title: string;
  collapsible?: boolean;
  isCollapsed?: boolean;
  onCollapseToggle?: () => void;
  primaryActionProps?: ButtonProps;
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
};

export const MainHeader = ({
  title,
  isCollapsed = false,
  onCollapseToggle,
  primaryActionProps,
  showBackButton = false,
  onBackButtonClick,
}: MainHeaderProps) => {
  const deviceWidth = useAppSelector(selectDeviceWidth);

  const location = useLocation();

  const { orderId } = useParams();

  const includeBottomPadding = useIncludeHeaderBottomPadding();

  const shouldShowHeaderNavigation =
    !!orderId && shouldShowHeaderNavigationForV2(location.pathname);

  const impersonationUserId = localStorage.getItem('impersonation-user-id');
  const account = useAppSelector(selectAccount);

  return (
    <Flex
      className="appraisify-main-header"
      height={shouldShowHeaderNavigation ? undefined : '48px'}
      minHeight={shouldShowHeaderNavigation ? undefined : '48px'}
      paddingBottom={includeBottomPadding ? SUBNAV_HEIGHT : undefined}
      width="100%"
      alignItems="center"
      justifyContent={shouldShowHeaderNavigation ? 'space-between' : undefined}
      paddingRight="20px"
      borderBottomWidth={borderWidth}
      borderColor={borderColor}
      borderStyle={borderStyle as BorderStyleProperty}
      whiteSpace="nowrap"
      gap="10px"
    >
      <MainHeaderOrderNavigation isCollapsed={isCollapsed} onCollapseToggle={onCollapseToggle} />
      <MainHeaderTitle
        title={title}
        shouldShowHeaderNavigation={shouldShowHeaderNavigation}
        showBackButton={showBackButton}
        onBackButtonClick={onBackButtonClick}
      />
      <Flex alignItems="center" gap="1rem">
        {impersonationUserId && (
          <Flex gap={2}>
            <Typography color="textWarning" variant="body2">
              Impersonating user: {`${account.firstName} ${account.lastName}`} (
              {account.email ?? ''})
            </Typography>
            <Button
              size="sm"
              text="Eject"
              onClick={() => {
                localStorage.removeItem('impersonation-user-id');
                window.location.replace('/');
              }}
              variant="danger"
            />
          </Flex>
        )}
        {isDefined(primaryActionProps) && <Button {...primaryActionProps} size="sm" />}
        <EnvironmentFilter environments={['web', 'regularMode']}>
          {deviceWidth !== 'phone' && (
            <Button
              text="FAQ"
              onClick={() => window.open(HELP_CENTER_URL, '_blank', 'noreferrer noopener')}
              size="sm"
              variant="secondary"
            />
          )}
        </EnvironmentFilter>
        <EnvironmentFilter environments={['web']}>
          <MainHeaderProfileMenu />
        </EnvironmentFilter>
        {!!orderId && (
          <EnvironmentFilter environments={['web', 'regularMode']}>
            <Tkv2ReferenceBarMobileToggle />
          </EnvironmentFilter>
        )}
      </Flex>
    </Flex>
  );
};
